import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Divider,
  InputAdornment,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import RpFieldLabel from "@/components/muiCustom/label/RpFieldLabel";
import RpVirtualList from "@/components/virtual/RpVirtualList";
import { shouldBlockUnsafeSearchInput } from "@/helpers/sanitizeHelper";
import { flattenGroupSections } from "../utils/locationManagementGroupTypes";

const uniqueValues = (values = []) => [...new Set(values.map(String))];
const GROUP_OPTION_ROW_HEIGHT = 40;
const GROUP_OPTION_LIST_MAX_HEIGHT = 240;
const GROUP_TRIGGER_RESERVED_WIDTH = 52;
const GROUP_TRIGGER_CHIP_GAP = 4;
const GROUP_TRIGGER_OVERFLOW_CHIP_WIDTH = 34;
const getEstimatedChipWidth = (label = "") =>
  Math.max(52, label.length * 7.2 + 16);

const LocationGroupFilter = ({
  groupSections = [],
  value = [],
  onChange,
  placeholder = "Groups",
}) => {
  const committed = useMemo(() => uniqueValues(value), [value]);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(committed);
  const [searchValue, setSearchValue] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [triggerWidth, setTriggerWidth] = useState(0);
  const anchorRef = useRef(null);

  useEffect(() => {
    setPending(committed);
  }, [committed]);

  useEffect(() => {
    setSelectedTypes((prev) =>
      prev.filter((type) =>
        groupSections.some((section) => section.type === type)
      )
    );
  }, [groupSections]);

  useEffect(() => {
    if (!anchorRef.current || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(([entry]) => {
      setTriggerWidth(entry.contentRect.width);
    });
    observer.observe(anchorRef.current);

    return () => observer.disconnect();
  }, []);

  const groupOptions = useMemo(
    () => flattenGroupSections(groupSections),
    [groupSections]
  );

  const selectedLabels = useMemo(() => {
    const selected = new Set(committed);
    return groupOptions
      .filter((group) => selected.has(group.value))
      .map((group) => group.label);
  }, [committed, groupOptions]);
  const triggerSelectedDisplay = useMemo(() => {
    if (!selectedLabels.length) return { labels: [], overflowCount: 0 };

    const availableWidth =
      triggerWidth > 0 ? triggerWidth - GROUP_TRIGGER_RESERVED_WIDTH : 224;
    const labels = [];
    let usedWidth = 0;

    selectedLabels.forEach((label, index) => {
      const remainingCount = selectedLabels.length - index - 1;
      const chipWidth = getEstimatedChipWidth(label);
      const overflowWidth = remainingCount
        ? GROUP_TRIGGER_CHIP_GAP + GROUP_TRIGGER_OVERFLOW_CHIP_WIDTH
        : 0;
      const nextWidth =
        usedWidth + (labels.length ? GROUP_TRIGGER_CHIP_GAP : 0) + chipWidth;

      if (nextWidth + overflowWidth <= availableWidth) {
        labels.push(label);
        usedWidth = nextWidth;
      }
    });

    if (!labels.length) {
      labels.push(selectedLabels[0]);
    }

    return {
      labels,
      overflowCount: selectedLabels.length - labels.length,
    };
  }, [selectedLabels, triggerWidth]);

  const visibleSections = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return groupSections
      .filter(
        (section) =>
          !selectedTypes.length || selectedTypes.includes(section.type)
      )
      .map((section) => ({
        ...section,
        groups: (section.groups || []).filter((group) =>
          normalizedSearch
            ? group.label.toLowerCase().includes(normalizedSearch) ||
              section.type.toLowerCase().includes(normalizedSearch)
            : true
        ),
      }))
      .filter((section) => section.groups.length);
  }, [groupSections, searchValue, selectedTypes]);

  const visibleGroupIds = useMemo(
    () => flattenGroupSections(visibleSections).map((group) => group.value),
    [visibleSections]
  );
  const visibleGroups = useMemo(
    () => flattenGroupSections(visibleSections),
    [visibleSections]
  );
  const selectedTypeGroupIds = useMemo(() => {
    if (!selectedTypes.length) return [];

    return flattenGroupSections(
      groupSections.filter((section) => selectedTypes.includes(section.type))
    ).map((group) => group.value);
  }, [groupSections, selectedTypes]);
  const pendingGroupIdsToApply = useMemo(() => {
    if (!selectedTypes.length) return pending;

    const selectedTypeGroupIdSet = new Set(selectedTypeGroupIds);
    return pending.filter((groupId) => selectedTypeGroupIdSet.has(groupId));
  }, [pending, selectedTypeGroupIds, selectedTypes.length]);
  const hasGroupsToApply = pendingGroupIdsToApply.length > 0;
  const hasCommittedGroups = committed.length > 0;

  const handleClose = () => {
    setPending(committed);
    setSearchValue("");
    setOpen(false);
  };

  const handleSearchChange = (event) => {
    const nextValue = event.target.value;
    if (shouldBlockUnsafeSearchInput(nextValue)) return;
    setSearchValue(nextValue);
  };

  const handleToggleGroup = useCallback((groupValue) => {
    setPending((prev) =>
      prev.includes(groupValue)
        ? prev.filter((valueItem) => valueItem !== groupValue)
        : [...prev, groupValue]
    );
  }, []);

  const renderGroupOption = useCallback(
    (group) => (
      <MenuItem
        key={group.value}
        onClick={() => handleToggleGroup(group.value)}
        sx={{ minHeight: 40, px: 0, py: 0.75 }}
      >
        <Checkbox
          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
          checkedIcon={<CheckBoxIcon fontSize="small" />}
          checked={pending.includes(group.value)}
          size="small"
          sx={{
            color: "var(--neutral-500)",
            pl: 0,
            pr: 1.25,
            py: 0,
            "&.Mui-checked": { color: "var(--primary-base)" },
          }}
        />
        <ListItemText
          primary={group.label}
          slotProps={{
            primary: {
              color: "var(--neutral-700)",
              fontSize: 13,
              fontWeight: 400,
              lineHeight: "19.5px",
            },
          }}
        />
      </MenuItem>
    ),
    [handleToggleGroup, pending]
  );

  const handleSelectAllVisible = () => {
    setPending((prev) => uniqueValues([...prev, ...visibleGroupIds]));
  };

  const handleToggleType = (type) => {
    setSelectedTypes((prev) => {
      if (!prev.includes(type)) {
        if (prev.length === 0) {
          const firstSelectedTypeGroupIds = new Set(
            flattenGroupSections(
              groupSections.filter((section) => section.type === type)
            ).map((group) => group.value)
          );

          setPending((pendingValues) =>
            pendingValues.filter((groupId) =>
              firstSelectedTypeGroupIds.has(groupId)
            )
          );
        }

        return [...prev, type];
      }

      const removedTypeGroupIds = new Set(
        flattenGroupSections(
          groupSections.filter((section) => section.type === type)
        ).map((group) => group.value)
      );

      setPending((pendingValues) =>
        pendingValues.filter((groupId) => !removedTypeGroupIds.has(groupId))
      );

      return prev.filter((selectedType) => selectedType !== type);
    });
  };

  const handleApply = () => {
    if (!hasGroupsToApply && !hasCommittedGroups) return;
    onChange?.(pendingGroupIdsToApply);
    setPending(pendingGroupIdsToApply);
    setOpen(false);
    setSearchValue("");
  };

  const handleClearCommitted = (event) => {
    event.stopPropagation();
    onChange?.([]);
    setPending([]);
    setSelectedTypes([]);
    setSearchValue("");
    setOpen(false);
  };

  const isApplyDisabled = !hasGroupsToApply && !hasCommittedGroups;
  const hasVisibleGroups = visibleGroupIds.length > 0;
  const groupListHeight = Math.min(
    visibleGroups.length * GROUP_OPTION_ROW_HEIGHT,
    GROUP_OPTION_LIST_MAX_HEIGHT
  );

  return (
    <Box>
      <RpFieldLabel>Groups</RpFieldLabel>
      <Box
        component="button"
        type="button"
        ref={anchorRef}
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          alignItems: "center",
          bgcolor: "var(--neutral-white)",
          border: "1px solid",
          borderColor: open ? "var(--primary-base)" : "var(--neutral-500)",
          boxShadow: open ? "inset 0 0 0 1px var(--primary-base)" : "none",
          borderRadius: "var(--radius-control)",
          color: "var(--neutral-700)",
          cursor: "pointer",
          display: "flex",
          font: "inherit",
          gap: 1,
          mt: "8px",
          minHeight: 40,
          px: 1.75,
          py: 0,
          textAlign: "left",
          width: "100%",
        }}
      >
        <Typography
          component="span"
          sx={{
            color: selectedLabels.length
              ? "var(--neutral-700)"
              : "var(--neutral-600)",
            flex: 1,
            fontSize: 14,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {!open && triggerSelectedDisplay.labels.length ? (
            <Box
              component="span"
              sx={{
                alignItems: "center",
                display: "flex",
                flex: 1,
                gap: `${GROUP_TRIGGER_CHIP_GAP}px`,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {triggerSelectedDisplay.labels.map((label, index) => (
                <Box
                  key={`${label}-${index}`}
                  component="span"
                  sx={{
                    alignItems: "center",
                    bgcolor:
                      "color-mix(in srgb, var(--palette-common-black) 8%, transparent)",
                    borderRadius: "var(--radius-control)",
                    color: "var(--neutral-700)",
                    display: "inline-block",
                    fontSize: 13,
                    fontWeight: 400,
                    height: 24,
                    justifyContent: "flex-start",
                    lineHeight: "23.5px",
                    minWidth: 0,
                    overflow: "hidden",
                    px: 1,
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </Box>
              ))}
              {triggerSelectedDisplay.overflowCount > 0 ? (
                <Box
                  component="span"
                  sx={{
                    alignItems: "center",
                    bgcolor:
                      "color-mix(in srgb, var(--palette-common-black) 8%, transparent)",
                    borderRadius: "var(--radius-control)",
                    color: "var(--neutral-700)",
                    display: "inline-flex",
                    fontSize: 13,
                    fontWeight: 400,
                    height: 24,
                    justifyContent: "center",
                    lineHeight: "23.5px",
                    px: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  +{triggerSelectedDisplay.overflowCount}
                </Box>
              ) : null}
            </Box>
          ) : (
            placeholder
          )}
        </Typography>
        {!open && selectedLabels.length ? (
          <Box
            component="span"
            aria-label="Clear selected groups"
            title="Clear selected groups"
            onClick={handleClearCommitted}
            sx={{
              alignItems: "center",
              borderRadius: "var(--radius-control)",
              color: "var(--neutral-700)",
              display: "inline-flex",
              flexShrink: 0,
              height: 20,
              justifyContent: "center",
              width: 20,
              "&:hover": {
                bgcolor:
                  "color-mix(in srgb, var(--palette-common-black) 8%, transparent)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </Box>
        ) : null}
        <KeyboardArrowDownIcon
          fontSize="small"
          sx={{
            color: "var(--neutral-500)",
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </Box>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1300, width: { xs: "calc(100vw - 32px)", sm: 498 } }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            sx={{
              border: "1px solid var(--neutral-200)",
              borderRadius: "var(--radius-container)",
              boxShadow: "var(--shadow-floating)",
              mt: 1,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                justifyContent: "space-between",
                px: 1.75,
                py: 1,
              }}
            >
              <Button
                variant="text"
                size="small"
                onClick={handleSelectAllVisible}
                disabled={!hasVisibleGroups}
                sx={{
                  fontSize: 11.2,
                  fontWeight: 500,
                  lineHeight: "19.6px",
                  minWidth: 64,
                  textTransform: "none",
                }}
              >
                Select All
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => setPending([])}
                disabled={pending.length === 0}
                sx={{
                  borderRadius: "var(--radius-control)",
                  fontSize: 11.2,
                  fontWeight: 500,
                  lineHeight: "19.6px",
                  minWidth: 64,
                  py: 0.25,
                  textTransform: "none",
                }}
              >
                Clear Selection
              </Button>
            </Box>
            <Divider />

            <Box sx={{ maxHeight: 402, overflowY: "auto", px: 2, py: 1.75 }}>
              <Typography
                sx={{
                  color: "var(--neutral-700)",
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: "19.92px",
                  mb: 1,
                }}
              >
                Filter by Group
              </Typography>
              <TextField
                value={searchValue}
                onChange={handleSearchChange}
                fullWidth
                size="small"
                placeholder="Search.."
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "var(--radius-control)",
                    minHeight: 45,
                  },
                  "& .MuiInputBase-input": {
                    fontSize: 12,
                    fontWeight: 400,
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "var(--neutral-600)",
                    opacity: 1,
                  },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Typography
                sx={{
                  color: "var(--neutral-700)",
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: "19.92px",
                  mb: 1.75,
                }}
              >
                Sort Groups By Type
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {groupSections.map((section) => {
                  const isSelected = selectedTypes.includes(section.type);

                  return (
                    <Button
                      key={section.type}
                      variant="outlined"
                      size="small"
                      onClick={() => handleToggleType(section.type)}
                      sx={{
                        bgcolor: isSelected
                          ? "var(--surface-dark)"
                          : "var(--neutral-white)",
                        borderColor: isSelected
                          ? "var(--surface-dark)"
                          : "var(--neutral-200)",
                        borderRadius: "var(--radius-control)",
                        color: isSelected
                          ? "var(--neutral-white)"
                          : "var(--neutral-700)",
                        fontSize: 13,
                        fontWeight: 400,
                        height: 24,
                        lineHeight: "19.5px",
                        minWidth: "auto",
                        px: 1,
                        py: 0,
                        textTransform: "none",
                        "&:hover": {
                          bgcolor: isSelected
                            ? "var(--surface-dark)"
                            : "var(--neutral-200)",
                          borderColor: isSelected
                            ? "var(--surface-dark)"
                            : "var(--neutral-200)",
                        },
                      }}
                    >
                      {section.type}
                    </Button>
                  );
                })}
              </Box>

              {visibleGroups.length ? (
                <Box sx={{ height: groupListHeight, minHeight: 40 }}>
                  <RpVirtualList
                    items={visibleGroups}
                    useWindowScroll={false}
                    style={{ height: "100%", overflowX: "hidden" }}
                    renderItem={renderGroupOption}
                  />
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: "var(--neutral-600)",
                    fontSize: 13,
                    py: 2,
                  }}
                >
                  No groups found.
                </Typography>
              )}
            </Box>

            <Divider />
            <Box sx={{ px: 2, py: 2 }}>
              <Button
                variant="contained"
                fullWidth
                disabled={isApplyDisabled}
                onClick={handleApply}
                sx={{
                  borderRadius: "var(--radius-control)",
                  fontSize: 12,
                  minHeight: 48,
                  textTransform: "none",
                }}
              >
                Apply Filters
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default LocationGroupFilter;
